import { Columns, FoldPanel, Specimens, Stack, Typography } from '@src/components';
import { BoxPicture, BOX_PICTURE_NAMES } from '@src/components/BoxPicture/BoxPicture';
import { PersonCard } from '@src/components/PersonCard/PersonCard';
import { ThemeArtwork } from '@src/theme/Artwork';
import { DigitCounter } from '@src/components/DigitCounter/DigitCounter';
import { useTheme } from '@src/theme/useTheme';
import { cardMotifs, type ArtworkRole } from '@src/theme/types';
import { CardMotif } from '@src/theme/Decoration';
import { DecoratedCard } from '@src/components/DecoratedCard/DecoratedCard';
import { edgeVariants } from '@src/components/PersonCard/cardAppearance';
import './ThemeArtworkSection.css';
import '@src/pages/BoxPage/full/HangingBoxMenu.css';
import {
  HangingGameSignArtwork,
  HangingGameSignDetails,
} from '@src/pages/BoxPage/full/HangingGameSign';

const roles: ArtworkRole[] = ['menu', 'menu-compact', 'exchange'];

/** One place to inspect the assets of every registered seasonal package. */
export function ThemeArtworkSection() {
  const { season } = useTheme();
  return (
    <FoldPanel
      id="season-artwork"
      title="Season artwork"
      hint={`Active package: ${season}. Original artwork proportions; live day/night colors.`}
    >
      <Stack gap={6} wide>
        <Specimens
          label="Live card decorations"
          note="The eight participant-card arrangements and five motifs supplied by the active theme."
        >
          <Columns columns={4} gap={6}>
            {edgeVariants.map((variant, index) => (
              <DecoratedCard key={variant} variant={variant} motif={false}>
                <div className="theme-artwork-specimen__card">
                  <Typography as="h3" role="title/sub">
                    Card {variant}
                  </Typography>
                  <CardMotif variant={cardMotifs[index % cardMotifs.length]} />
                </div>
              </DecoratedCard>
            ))}
          </Columns>
        </Specimens>
        <Specimens label="Game pictures · stable saved identifiers">
          <div className="theme-game-pictures">
            {BOX_PICTURE_NAMES.map((picture) => (
              <figure key={picture}>
                <BoxPicture picture={picture} />
                <figcaption>{picture}</figcaption>
              </figure>
            ))}
          </div>
        </Specimens>
        <Specimens label="Participant card text">
          <PersonCard
            layout="exchange"
            compact
            title="MY CARD"
            name="Create a card"
            status={
              <Typography as="p" role="body/sm" color="inherit">
                The draw is complete. Creating new participant cards is unavailable.
              </Typography>
            }
          />
        </Specimens>
        <Specimens label="Game panel with content">
          <HangingGameSignArtwork name="Office draw 2026">
            <HangingGameSignDetails
              organizer="Organizer: Nina"
              status="The draw has not happened yet"
            />
          </HangingGameSignArtwork>
        </Specimens>
        <Specimens label="Counter with live content">
          <div className="theme-artwork-specimen__counter">
            <DigitCounter value={93463} label="Participants this year" />
          </div>
        </Specimens>
        {roles.map((role) => (
          <div key={role} className="theme-artwork-specimen">
            <Typography as="h3" role="title/sub">
              {role}
            </Typography>
            {role === 'exchange' ? (
              <ThemeArtwork role={role} className="theme-artwork-specimen__image" />
            ) : (
              <div
                className={`hanging-box-menu${role === 'menu-compact' ? ' hanging-box-menu--compact' : ''}`}
              >
                <ThemeArtwork role={role} className="hanging-box-menu__art" />
              </div>
            )}
          </div>
        ))}
      </Stack>
    </FoldPanel>
  );
}
